export interface MainMenuConfig {
	toggles: string;
	styleClass: {
		menuOpen: string;
	};
	focusLock: {
		dialog: string;
		return: string;
		toggle: string;
	};
	easing: string;
	duration: number;
}

export class MainMenu {
	private readonly mouseOutHideDelay: number = 50;
	private focusFirstElement: HTMLElement|null = null;
	private focusLastElement?: HTMLElement;
	private static menuLUID = 0;

	constructor(
		private readonly menu: HTMLElement,
		private readonly toggleOpen: HTMLElement,
		private readonly config: MainMenuConfig
	) {
		// Allows for non-js fallbacks
		menu.classList.add('js-ready');

		this.hydrateMenuSubtree();

		// Toggles mobile menu based on if this.toggleOpen is on screen
		new IntersectionObserver((entries) => {
			for (const entry of entries) {
				// kinda gross, but there's just a lot of ternary
				const isDialog = entry.intersectionRatio > 0;
				// AXE devtools complains about aria-modal not being valid if "false"
				if (isDialog) {
					menu.setAttribute('aria-modal', 'true');
				} else {
					menu.removeAttribute('aria-modal');
				}
				menu.setAttribute('data-was', menu.getAttribute('role') ?? 'initial');
				menu.setAttribute('role', isDialog ? 'dialog' : '');
				// Client requested top-level menu items with children to NOT be links on desktop so this code is getting moved
				/* for (const element of menu.querySelectorAll('.menu-item-has-children > a[aria-expanded]')) {
					element.ariaHidden = isDialog ? 'true' : 'false';
				} */
				if (isDialog) {
					menu.addEventListener("transitionend", () => {
						menu.removeAttribute('data-was');
					}, {once: true});
				}
			}
		}).observe(toggleOpen);

		// Client requested top-level menu items with children to NOT be links on desktop
		for (const element of menu.querySelectorAll('.menu-item-has-children > a[aria-expanded]')) {
			element.ariaHidden = 'true';
		}

		this.loadPotentialAJAXMenu(menu); //TODO: Only run if on mobile
	}

	private createButtonTemplate() {
		// Creates a button to be cloned later
		const icon = document.createElement('span');
		icon.classList.add('icon');
		icon.setAttribute('aria-hidden', 'true');

		const text = document.createElement('span');
		// Client requested top-level menu items with children to NOT be links on desktop
		//text.classList.add('text', 'sr-desktop');
		text.classList.add('text');
		text.innerHTML = '<span class="screen-reader-text">submenu</span>';

		const button = document.createElement('button');
		button.setAttribute('aria-expanded', 'false');
		button.append(text, icon);

		return button;
	}
	private createButtons(menuSubtreeRoot: HTMLElement): void {
		const buttonTemplate = this.createButtonTemplate();

		const kebabCaseRegexes = [/([a-z])([A-Z])/g, /[\s_]+/g] as const;
		// Presumptions - there are anchors, with textContent
		for (const item of menuSubtreeRoot.querySelectorAll('.menu-item-has-children')) {
			const anchor = item.querySelector('a')!;

			// gets properties for aria attributes
			const name = anchor.textContent ?? '';

			const kebabCaseName = name
				.replace(kebabCaseRegexes[0], "$1-$2")
				.replace(kebabCaseRegexes[1], '-')
				.toLowerCase();
			const menuId = `menu-${kebabCaseName}-${MainMenu.menuLUID}`;
			const labelId = `label-${kebabCaseName}-${MainMenu.menuLUID}`;

			anchor.setAttribute('id', labelId);
			anchor.setAttribute('aria-expanded', 'false');

			const submenu = item.querySelector('.sub-menu');
			if (submenu) {
				submenu.setAttribute('id', menuId);
				submenu.setAttribute('aria-labelledby', labelId);
			}

			const button = buttonTemplate.cloneNode(true) as HTMLButtonElement;
			button.setAttribute('aria-controls', menuId);
			button.querySelector('.text')?.prepend(name + ' ');

			item.insertBefore(button, submenu);
		}
	}

	private hydrateMenuSubtree(menuSubtreeRoot: HTMLElement = this.menu) {
		this.createButtons(menuSubtreeRoot);
		this.setupEventListeners(menuSubtreeRoot);
		MainMenu.menuLUID++;
	}

	private setupEventListeners(menuSubtreeRoot: HTMLElement): void {
		for (const item of menuSubtreeRoot.querySelectorAll<HTMLElement>('.menu-item-has-children')) {
			let mouseOutTimer: any;

			// Show the menu on mouse hover of the trigger, Ignore on mobile
			item.addEventListener('pointerenter', event => {
				if (this.menu.ariaModal === 'true') return;

				this.submenuShow(event.target as Element);
				clearTimeout(mouseOutTimer);
			});

			/* Hide the menu when mouse hover leaves both the trigger and menu fly-out, but only after a short delay to help people jerky mouse movements (like those using head/eye trackers), Ignore on mobile */
			item.addEventListener('pointerleave', event => {
				if (this.menu.ariaModal === 'true') return;

				mouseOutTimer = setTimeout(() => {
					this.submenuHide(event.target as Element);
				}, this.mouseOutHideDelay);
			});

			// Hide the menu when the user tabs out or escapes, Ignore tabs on mobile
			item.addEventListener('keydown', event => {
				let currentItem: HTMLElement|null|undefined = item;
				if (event.key === 'Escape') {
					// allows for deep backing out
					if (item.querySelector('a')?.ariaExpanded === 'false') {
						currentItem = item.parentElement?.closest('li');
						if (!currentItem) {
							return;
						}
					}
					event.stopPropagation();
					this.submenuHide(currentItem);
					currentItem.querySelector('button')?.focus();
				}

				if (this.menu.ariaModal === 'true') return;

				const lastItem = currentItem.querySelector(":scope > .sub-menu > li:last-child a");
				const firstItem = currentItem.querySelector(":scope > .sub-menu > li:first-child a");
				if (
					(event.key === 'Tab' && !event.shiftKey && event.target === lastItem) ||
					(event.key === 'Tab' && event.shiftKey && event.target === firstItem)
				) {
					this.submenuHide(currentItem);
				}
			});

			// Toggle the menu when the trigger is activated
			for (const button of item.querySelectorAll<HTMLButtonElement>(':scope > button')) {
				button.addEventListener('click', event => {
					if (event.target instanceof HTMLElement) {
						this.submenuToggle(event.target.closest('li'));
					}
				});
			}
		}

		if (menuSubtreeRoot === this.menu) {
			// Close the menu when the user activates something outside the navbar.
			document.body.addEventListener('click', event => {
				if (!(event.target instanceof HTMLElement) || this.menu.contains(event.target)) return;
				if (this.menu.ariaModal === 'true' && this.toggleOpen == event.target.closest('a')) return;

				for (const button of this.menu.querySelectorAll('button[aria-expanded="true"]')) {
					const wrapper = button.closest('li');
					if (wrapper) {
						this.submenuHide(wrapper);
					}
				}
			});

			// Open/close menu
			for (const element of document.querySelectorAll(this.config.toggles)) {
				element.addEventListener('click', event => {
					event.preventDefault();
					this.modalToggle();
				});
			}
		}
	}

	private modalFocusLock(open: boolean) {
		const dialog = document.querySelector(this.config.focusLock.dialog);
		if (!dialog) {
			return;
		}

		const focusLockEventHandler = (event: KeyboardEvent) => {
			if (event.key === 'Tab') {
				if (event.target === this.focusFirstElement && event.shiftKey) {
					event.preventDefault();
					this.focusLastElement?.focus();
				} else if (event.target === this.focusLastElement && !event.shiftKey) {
					event.preventDefault();
					this.focusFirstElement?.focus();
				}
			}
		};

		if (open) {
			this.focusFirstElement = document.querySelector<HTMLElement>(this.config.focusLock.toggle);
			this.focusLastElement = Array.from(dialog.querySelectorAll<HTMLElement>('a, button')).pop();

			this.menu.addEventListener('keydown', focusLockEventHandler);
			this.focusFirstElement?.focus();
		} else {
			const focusReturnElement = document.querySelector<HTMLElement>(this.config.focusLock.return);
			this.menu.removeEventListener('keydown', focusLockEventHandler);
			focusReturnElement?.focus();
		}
	}

	private modalToggle(open?: boolean): void {
		if (open === undefined) {
			open = !document.body.classList.contains(this.config.styleClass.menuOpen);
		}
		// Menu Open class
		document.body.classList.toggle(this.config.styleClass.menuOpen);
		// Scroll locking
		document.querySelector('html')?.classList.toggle('lockScroll');
		// Toggle button focus: Relies on --close, --open naming convention
		document.querySelector<HTMLElement>(this.config.toggles + (open ? '--close' : '--open'))?.focus();

		this.modalFocusLock(open);

		// Open current
		if (open) {
			for (const element of this.menu.querySelectorAll('li[class*="current"]')) {
				this.submenuShow(element);
			}
		}
	}

	private submenuHide(item: Element): void {
		for (const element of item.querySelectorAll(':scope > a, :scope > button')) {
			element.setAttribute('aria-expanded', 'false');
		}
	}
	private submenuShow(item: Element): void {
		for (const element of item.querySelectorAll(':scope > a, :scope > button')) {
			element.setAttribute('aria-expanded', 'true');
		}
	}
	private submenuToggle(item: Element|null): void {
		if (!item) {
			return;
		}
		if (item.querySelector('a')?.getAttribute('aria-expanded') === 'true') {
			this.submenuHide(item);
		} else {
			this.submenuShow(item);
		}
	}

	private loadPotentialAJAXMenu(menuRoot: HTMLElement) {
		// AJAX load a new version of the HTML for wrapper
		const container = menuRoot.querySelector<HTMLElement>('div[data-plaudit-menu-url]');
		if (!container) {
			return;
		}

		const menuURL = container.getAttribute('data-plaudit-menu-url');
		if (menuURL) {
			fetch(menuURL).then(response => {
				if (response.ok) {
					response.text().then(text => {
						const ajaxMenu = (new DOMParser()).parseFromString(text, "text/html").body.firstElementChild as HTMLElement;

						// Loaded menu does not have the current item marked with a class, so we need to do that
						// by looking for a link with the current URL, and if that doesn't exist then the first link
						// with the current URL but excluding the fragment identifier
						// NOTE: When developing locally this will not work on port 3000 since response comes back with 8443
						const currentURL = window.location.href;
						const currentLink = ajaxMenu.querySelector<HTMLElement>(`a[href="${currentURL}"], a[href="${currentURL.split('#')[0]}"]`);
						if (currentLink) {
							const currentItem = currentLink.closest('li');
							if (currentItem) {
								// Current
								currentItem.classList.add('current-menu-item');

								let parent = currentItem.parentNode;
								let parentFound = false;
								while (parent instanceof HTMLElement && parent !== ajaxMenu) {
									if (parent.classList.contains('sub-menu')) {
										if (!parentFound) {
											// Parent
											parentFound = true;
											parent.classList.add('current-menu-parent');
										}

										// Ancestor
										parent.classList.add('current-menu-ancestor');
									}
									parent = parent.parentNode;
								}
							}
						}

						// Treat as new menu
						// First the first item marked current.
						let currentItem: HTMLElement|null|undefined = null;
						for (const selector of ['.current-menu-item > ul > .current-menu-item > a',    // First find 'Overview' menu item
							'.current-menu-item > :is(button, a)']) {             // Then a link or button
							currentItem = ajaxMenu.querySelector<HTMLElement>(selector);
							if (currentItem)
								break;
						}

						if (currentItem) {
							// Open all ancestors
							let parent = currentItem.parentNode;
							while (parent instanceof HTMLElement && parent !== ajaxMenu) {
								if (parent.classList.contains('sub-menu')) {
									const button = parent.closest('li')?.querySelector<HTMLElement>('.mobile-menu-item--button');
									if (button) {
										this.submenuShow(button);
									}
								}
								parent = parent.parentNode;
							}
						}

						container.parentElement!.append(ajaxMenu);
						this.hydrateMenuSubtree(ajaxMenu);
					});
				} else {
					console.error('Failed to load menu HTML');
				}
			});
		}
	}
}
